import type { MemberCountryCode } from "../location/memberLocation";
import {
  AUSTRALIA_APPENDIX_ROUTE_LABELS,
} from "./supportAustralia";
import { getNewZealandStaticSupportCategoryCards } from "./supportNewZealandCards";
import { getUnitedStatesStaticSupportCategoryCards } from "./supportUsCards";
import {
  SUPPORT_CANONICAL_SECTION,
  getCanonicalSupportCategory,
} from "./supportCards";

export type StaticSupportContentCard = {
  id: string;
  section: string;
  category: string;
  country_scope: MemberCountryCode;
  title: string;
  body: string;
  link_url: string | null;
  sort_order: number;
  last_reviewed_at: string;
  is_active: boolean;
};

type AustraliaSupportCardDefinition = {
  title: string;
  summary: string;
  routes: string[];
};

const AUSTRALIA_SUPPORT_REVIEWED_AT = "2026-04-21T12:00:00.000Z";

const buildBody = (summary: string, routes: string[]) =>
  `${summary}\n\nOfficial routes:\n${routes.map((route) => `- ${route}`).join("\n")}`;

const buildCategoryCards = (
  slug: string,
  definitions: AustraliaSupportCardDefinition[],
): StaticSupportContentCard[] =>
  definitions.map((definition, index) => ({
    id: `static-au-${slug}-${index + 1}`,
    section: SUPPORT_CANONICAL_SECTION,
    category: getCanonicalSupportCategory(slug),
    country_scope: "AU",
    title: definition.title,
    body: buildBody(definition.summary, definition.routes),
    link_url: null,
    sort_order: index + 1,
    last_reviewed_at: AUSTRALIA_SUPPORT_REVIEWED_AT,
    is_active: true,
  }));

const AUSTRALIA_SUPPORT_CARD_DEFINITIONS: Record<
  string,
  AustraliaSupportCardDefinition[]
> = {
  "money-benefits": [
    {
      title: "Parenting Payment — who it is for and how to claim",
      summary:
        "Check who may qualify, how claims work, what mutual obligations may apply, and why you should update Services Australia when work, care, or relationship circumstances change. Use the live page because rules and rates can move.",
      routes: ["Services Australia — Parenting Payment"],
    },
    {
      title: "Family Tax Benefit — Parts A and B",
      summary:
        "Use this to understand who may qualify, how income and care arrangements affect payment, and why end-of-year balancing matters for separated parents.",
      routes: ["Services Australia — Family Tax Benefit"],
    },
    {
      title: "Child Care Subsidy — reducing formal childcare costs",
      summary:
        "Check how approved care, activity settings, income, and service type affect support, and update your estimate if work or care changes.",
      routes: ["Services Australia — Child Care Subsidy"],
    },
    {
      title: "Child support — assessment, collection and private agreements",
      summary:
        "Use this to understand assessment, Services Australia collection, private collection, and what to review if care levels, costs, or payment reliability change.",
      routes: ["Services Australia — Child Support"],
    },
    {
      title: "Separated parents — what to update after a breakup",
      summary:
        "After separation, update family payments, child support, Medicare details, and any recorded care percentages early so the system matches real life.",
      routes: [
        "Services Australia — Family Tax Benefit",
        "Services Australia — Child Support",
      ],
    },
    {
      title: "Rent Assistance — if you pay rent",
      summary:
        "Check whether Rent Assistance may apply alongside your main payment, how proof of rent works, and why address or rent changes should be updated quickly.",
      routes: ["Services Australia — Rent Assistance"],
    },
    {
      title: "Parental Leave Pay — what it covers",
      summary:
        "Use the live Parental Leave Pay page for eligibility, claim timing, and how the payment sits alongside employer leave, because duration and settings can change over time.",
      routes: ["Services Australia — Parental Leave Pay"],
    },
    {
      title: "Low Income Health Care Card — lowering everyday costs",
      summary:
        "Check who may qualify, what this card can reduce, and then follow through into state and territory concessions once the card is in place.",
      routes: ["Services Australia — Low Income Health Care Card"],
    },
    {
      title: "Disability and caring payments for children — where to start",
      summary:
        "Start here for carer payments and child disability support, and remember that NDIS and Centrelink are different systems with different rules.",
      routes: ["Services Australia — Support for carers"],
    },
    {
      title: "Crisis Payment and advance payments — short-term help in a crisis",
      summary:
        "If crisis or family violence has knocked things sideways, check the current crisis-payment route or an advance-payment option quickly instead of waiting for the next regular cycle.",
      routes: ["Services Australia — Crisis Payment"],
    },
    {
      title: "Budgeting on one income — start with Moneysmart",
      summary:
        "Use a simple budget to separate essentials from everything else, especially when school holidays, big bills, or a change in care has squeezed the month.",
      routes: ["Moneysmart — Budgeting"],
    },
    {
      title: "Hardship and debt help — ask early",
      summary:
        "If rent, utilities, loans, or credit cards are becoming unmanageable, contact hardship routes and a free financial counsellor early rather than waiting for the problem to harden.",
      routes: ["National Debt Helpline — Emergency assistance"],
    },
    {
      title: "No Interest Loans — when they are a safer option",
      summary:
        "Use this route when an essential item breaks or a life-admin cost lands and you need a safer alternative to payday credit or buy now pay later.",
      routes: ["Moneysmart — No Interest Loans"],
    },
    {
      title: "Superannuation as a single parent — do not leave it until later",
      summary:
        "Check employer super, lost accounts, and what reduced or interrupted work can do over time so you can keep track without trying to solve everything at once.",
      routes: ["Moneysmart — Superannuation"],
    },
  ],
  housing: [
    {
      title: "Renting as a single parent — your rights depend on your state or territory",
      summary:
        "Tenancy law is state and territory based, so start with the authority that applies where you live for rent, written agreements, discrimination, notice rules, and tribunal pathways.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.tenancy],
    },
    {
      title: "Bond, entry condition reports and evidence",
      summary:
        "Use your local tenancy authority to check bond handling, condition reports, and the evidence you should gather at the start of a tenancy to protect yourself later.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.tenancy],
    },
    {
      title: "Repairs, minimum standards and safety",
      summary:
        "Urgent repairs, minimum standards, smoke alarms, hot water, and written repair requests all depend on the rules where you live, so use the local authority first.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.tenancy],
    },
    {
      title: "Rent increases, arrears and hardship",
      summary:
        "If rent is rising or arrears are building, check the local notice and challenge rules early and ask for hardship arrangements before the position worsens.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.tenancy],
    },
    {
      title: "Ending a tenancy, notices and eviction",
      summary:
        "Notice periods, fixed-term endings, periodic leases, and eviction steps vary a lot by state and territory, so use the right local process as soon as a notice lands.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.tenancy],
    },
    {
      title: "Family and domestic violence and housing",
      summary:
        "If housing pressure is tangled up with family violence, start with national safety support and then move into your local tenancy protections for lease changes, security, or urgent accommodation.",
      routes: [
        "1800RESPECT",
        AUSTRALIA_APPENDIX_ROUTE_LABELS.tenancy,
      ],
    },
    {
      title: "Social housing and community housing — how the system works",
      summary:
        "Use your state or territory housing authority for applications, waitlists, documents, and priority categories, because social housing rules are not run as one national system.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.socialHousing],
    },
    {
      title: "Moving house with children — the legal and practical bits",
      summary:
        "A move can affect school, childcare, parenting arrangements, bond return, and sometimes consent or legal advice if the distance is significant.",
      routes: [
        "Family Relationships Online",
        AUSTRALIA_APPENDIX_ROUTE_LABELS.educationStart,
      ],
    },
    {
      title: "Home ownership as a single parent — what government help exists",
      summary:
        "Use the current national home-ownership scheme pages for the up-to-date position on single-parent support, because scheme settings can change over time.",
      routes: ["Housing Australia — Home Guarantee Scheme"],
    },
    {
      title: "Energy and household bills — concessions and hardship",
      summary:
        "Check retailer hardship programs first, then use your Commonwealth cards and local concessions together rather than assuming one route does all the work.",
      routes: [
        "Moneysmart — Bills and concessions",
        "Services Australia — Low Income Health Care Card",
      ],
    },
  ],
  legal: [
    {
      title: "Separation and divorce — the basics in Australia",
      summary:
        "Start here to separate divorce, parenting, and property into the right legal tasks instead of assuming one process solves everything.",
      routes: ["FCFCOA — Separation and divorce"],
    },
    {
      title: "Parenting arrangements — parenting plans, consent orders and court orders",
      summary:
        "Use this to understand the difference between informal arrangements, written agreements, parenting plans, and court orders.",
      routes: [
        "Family Relationships Online",
        "FCFCOA — Parenting orders",
      ],
    },
    {
      title: "Family dispute resolution — when you usually need it first",
      summary:
        "Many families need family dispute resolution before a parenting application, unless an exemption applies, so start with the national family-relationship route early.",
      routes: ["Family Relationships Online"],
    },
    {
      title: "Applying for parenting orders — where to start",
      summary:
        "If you cannot resolve arrangements, use the court’s national parenting-orders guidance and get legal help early if safety, relocation, or urgency are in the picture.",
      routes: [
        "FCFCOA — Parenting orders",
        AUSTRALIA_APPENDIX_ROUTE_LABELS.legalAid,
      ],
    },
    {
      title: "Child support and parenting arrangements — different systems",
      summary:
        "Keep parenting arrangements and child support separate in your head and in your paperwork, because mixing them up usually makes both harder.",
      routes: [
        "Services Australia — Child Support",
        "Family Relationships Online",
      ],
    },
    {
      title: "Moving interstate or overseas with children — when consent matters",
      summary:
        "If you are considering a major move, especially overseas, get advice before booking travel or relocating because consent and court issues can escalate fast.",
      routes: [
        "FCFCOA — Parenting orders",
        AUSTRALIA_APPENDIX_ROUTE_LABELS.legalAid,
      ],
    },
    {
      title: "Child passports and parental consent",
      summary:
        "Use the Passport Office’s child-consent guidance first if another parent will not sign, if court orders are involved, or if you need to understand the correct route before travel.",
      routes: [
        "Passport Office — Parental consent",
        "Passport Office — Child alerts",
      ],
    },
    {
      title: "Family violence and protection orders",
      summary:
        "Protection orders, court names, and enforcement paths vary by state and territory, so start with national safety support and then move into local legal help quickly.",
      routes: [
        "1800RESPECT",
        AUSTRALIA_APPENDIX_ROUTE_LABELS.legalAid,
      ],
    },
    {
      title: "Free legal help — legal aid and community legal centres",
      summary:
        "For family law, child support, tenancy, debt, and safety issues, use national find-help routes plus your state or territory legal aid entry point.",
      routes: [
        "National Legal Aid — Find legal help",
        "Community Legal Centres Australia — Find legal help",
        AUSTRALIA_APPENDIX_ROUTE_LABELS.legalAid,
      ],
    },
    {
      title: "Wills, guardianship and powers of attorney",
      summary:
        "Single parents should not leave wills, guardianship wishes, and powers of attorney until later. The practical rules for powers of attorney are state and territory based, so use local legal help if you are unsure.",
      routes: [
        "Moneysmart — Wills and powers of attorney",
        AUSTRALIA_APPENDIX_ROUTE_LABELS.legalAid,
      ],
    },
  ],
  wellbeing: [
    {
      title: "When the house is quiet — coping when the children are away",
      summary:
        "The quiet can hit hard after handovers or changed care, and it is okay to treat that as something that may need support rather than just more distraction.",
      routes: ["Medicare Mental Health"],
    },
    {
      title: "Loneliness and isolation — rebuilding adult connection",
      summary:
        "Use this when adult connection has fallen away and isolation is turning into something heavier than an ordinary hard patch.",
      routes: ["Medicare Mental Health"],
    },
    {
      title: "Burnout — noticing it earlier",
      summary:
        "If you are the only reliable adult in the household, burnout can look like numbness, irritation, forgetfulness, or feeling constantly behind. It is worth talking to a GP earlier than you think.",
      routes: ["healthdirect — Mental health and wellbeing"],
    },
    {
      title: "Guilt and impossible standards — getting back to good enough",
      summary:
        "Use this when work guilt, money guilt, or separation guilt are crowding out the practical support you actually need.",
      routes: ["healthdirect — Mental health and wellbeing"],
    },
    {
      title: "After separation — rebuilding life one bit at a time",
      summary:
        "Separation is often administratively and emotionally messy for longer than people admit, so start with one safe next support instead of trying to fix the whole future at once.",
      routes: ["Family Relationships Online"],
    },
    {
      title: "Mental health support — start with a GP or Medicare Mental Health",
      summary:
        "A GP, a mental health treatment plan, or Medicare Mental Health can all be sensible first steps when you need practical support, not just encouragement to keep pushing through.",
      routes: [
        "Medicare Mental Health",
        "healthdirect — Mental health and wellbeing",
      ],
    },
    {
      title: "Crisis support — when you need help now",
      summary:
        "If tonight feels unsafe, unbearable, or too much to hold, use crisis support first. There is no need to have the legal or housing side figured out before asking for help.",
      routes: ["Lifeline"],
    },
    {
      title: "Family violence support — when safety is part of the picture",
      summary:
        "If violence, coercion, or fear are in the picture, start with specialist family-violence support and treat safety as the first task, not the last.",
      routes: ["1800RESPECT"],
    },
    {
      title: "Sleep — when exhaustion is running the show",
      summary:
        "Poor sleep can turn every other problem up. Start with practical sleep support and involve a GP if hypervigilance, anxiety, or insomnia are driving the pattern.",
      routes: ["healthdirect — Mental health and wellbeing"],
    },
    {
      title: "Exercise and movement on a single-parent schedule",
      summary:
        "This is about realistic movement that supports mood, sleep, and stress on the schedule you actually have, not an ideal one.",
      routes: ["healthdirect — Mental health and wellbeing"],
    },
  ],
  "education-send": [
    {
      title: "School enrolment — the rules are set by your state or territory",
      summary:
        "Catchments, timelines, documents, and mid-year moves are handled locally, so use your state or territory education entry point first.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.educationStart],
    },
    {
      title: "Starting school and changing schools — what to organise early",
      summary:
        "Start school or move-school planning early if family arrangements, travel, or support needs are changing, because the first conversations matter.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.educationStart],
    },
    {
      title: "School costs, fees and what to ask about",
      summary:
        "Uniforms, devices, excursions, camps, and payment plans vary, so ask the school early and use your state or territory education route for the local policy position.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.educationStart],
    },
    {
      title: "Suspensions, exclusions and school discipline — know the process",
      summary:
        "Suspensions, exclusions, written reasons, and review rights vary by jurisdiction, so use your state or territory education route and keep the written timeline clear.",
      routes: [
        AUSTRALIA_APPENDIX_ROUTE_LABELS.educationStart,
        "Disability Standards for Education",
      ],
    },
    {
      title: "Home education — registration comes first",
      summary:
        "Home education is lawful, but registration, reviews, and expectations vary, so do not withdraw your child from school before checking the correct local process.",
      routes: [AUSTRALIA_APPENDIX_ROUTE_LABELS.homeEducation],
    },
    {
      title: "Childcare options explained — long day care, family day care, OSHC and nannies",
      summary:
        "Use this to compare the main childcare types and think practically about what fits school-age care, irregular work, or holiday pressure.",
      routes: ["Starting Blocks"],
    },
    {
      title: "Use Child Care Subsidy and Starting Blocks together",
      summary:
        "Use Services Australia for the subsidy rules and Starting Blocks for vacancies, fees, ratings, and service comparisons so you are not guessing the real picture.",
      routes: [
        "Services Australia — Child Care Subsidy",
        "Starting Blocks",
      ],
    },
    {
      title: "Preschool or kindergarten before school — delivery differs by state or territory",
      summary:
        "Every child can access a year of preschool or kindergarten before school, but delivery differs by location, so use local education guidance early if you are moving or comparing options.",
      routes: [
        AUSTRALIA_APPENDIX_ROUTE_LABELS.educationStart,
        "Starting Blocks",
      ],
    },
    {
      title: "Holiday care and outside school hours care",
      summary:
        "Vacation care and OSHC often need organising earlier than parents expect. Use the childcare finder and subsidy rules together so you can see the real options.",
      routes: [
        "Starting Blocks",
        "Services Australia — Child Care Subsidy",
      ],
    },
    {
      title: "Disability support at school — start with reasonable adjustments",
      summary:
        "Do not wait for a perfect label before asking for support. Start with the adjustments the school can make now and keep the requests in writing.",
      routes: ["Disability Standards for Education"],
    },
    {
      title: "Disability Standards for Education — your child’s rights",
      summary:
        "Use the Disability Standards as the national rights backbone for consultation, participation, and support when disability affects school access or inclusion.",
      routes: ["Disability Standards for Education"],
    },
    {
      title: "NDIS and school support — what sits where",
      summary:
        "Schools and NDIS do different jobs. Use the national NDIS school-support guidance so you can ask clearly what should sit with the school and what may sit in a plan.",
      routes: ["NDIS — Education and school supports"],
    },
    {
      title: "Early childhood concerns and early intervention — where to start if you are worried",
      summary:
        "If you are worried before school, ask what health, early-childhood, and disability supports can begin now instead of waiting for the problem to become bigger.",
      routes: [
        "NDIS — Education and school supports",
        "Starting Blocks",
      ],
    },
    {
      title: "ADHD and autism — referral and support pathways",
      summary:
        "Start with a GP, paediatrician, psychologist, or school conversation, and keep school support moving while assessment or diagnosis is still in progress.",
      routes: ["healthdirect — ADHD and autism"],
    },
    {
      title: "Learning difficulties such as dyslexia or dyscalculia — getting help early",
      summary:
        "Use this to spot the pattern early, understand where assessment may sit, and keep written requests moving while you gather evidence.",
      routes: ["healthdirect — Learning difficulties"],
    },
  ],
  work: [
    {
      title: "Flexible work arrangements — asking for flexibility",
      summary:
        "Use the Fair Work guidance to understand who can request flexibility, how to ask, when an employer can refuse, and why written requests matter.",
      routes: ["Fair Work — Flexible working arrangements"],
    },
    {
      title: "Parents and carers at work — leave and basic protections",
      summary:
        "Start here for unpaid parental leave, return-to-work rights, and the baseline protections that matter when work and care are colliding.",
      routes: ["Fair Work — Parental leave"],
    },
    {
      title: "Paid family and domestic violence leave",
      summary:
        "Use this route if work is part of the safety problem or you need protected time for practical, medical, legal, or safety steps.",
      routes: ["Fair Work — Family and domestic violence leave"],
    },
    {
      title: "Personal and carer’s leave — when your child is sick or care falls through",
      summary:
        "Check paid and unpaid leave rules, evidence requests, and what casual workers should look at when care breaks down unexpectedly.",
      routes: ["Fair Work — Sick and carer’s leave"],
    },
    {
      title: "Casual and part-time work — check the terms before saying yes",
      summary:
        "Use this to compare rosters, loading, regular patterns, and permanence options before you accept a job that may look flexible but behave badly in practice.",
      routes: ["Fair Work — Casual and part-time work"],
    },
    {
      title: "Minimum pay, awards and being paid correctly",
      summary:
        "Check the national minimum wage, award coverage, penalty rates, allowances, and payslips rather than assuming the employer has set it up correctly.",
      routes: ["Fair Work — Pay, awards and entitlements"],
    },
    {
      title: "Redundancy, notice and final pay",
      summary:
        "If a role ends, check notice, leave, final pay, and redundancy rules instead of relying on verbal reassurance that everything has been handled correctly.",
      routes: ["Fair Work — Redundancy and final pay"],
    },
    {
      title: "Discrimination or adverse action because you are a parent or carer",
      summary:
        "If treatment changes after leave, flexibility, pregnancy, or caring requests, use the workplace-protections route early rather than waiting for the position to harden.",
      routes: ["Fair Work — Workplace protections and discrimination"],
    },
    {
      title: "Returning to work after a gap",
      summary:
        "Plan childcare first, recheck payments second, and ask practical flexibility questions early so a return to work is sustainable in real life rather than only on paper.",
      routes: [
        "Fair Work — Flexible working arrangements",
        "Services Australia — Child Care Subsidy",
      ],
    },
    {
      title: "Sole trader or company — choose the structure before you rush in",
      summary:
        "Use the official business-structure guidance before you trade properly so you understand liability, admin, tax, and what staying small can look like safely.",
      routes: ["business.gov.au — Business structure and registrations"],
    },
    {
      title: "Starting self-employment — ABN, GST and registrations",
      summary:
        "Start with the business registration route instead of guessing from social media, especially for ABN, GST, and business-name questions.",
      routes: ["business.gov.au — Business structure and registrations"],
    },
    {
      title: "Record keeping, BAS and tax time — make it a habit",
      summary:
        "Set up a simple weekly habit early so income, expenses, mileage, and tax admin stay legible even when life is busy.",
      routes: ["ATO — Record keeping for business"],
    },
    {
      title: "Superannuation when you are self-employed or working less",
      summary:
        "Use this when self-employment or reduced hours are changing what happens to super, or when fragmented work has left lost accounts behind you.",
      routes: ["Moneysmart — Superannuation"],
    },
  ],
};

export const getStaticSupportCategoryCards = (
  slug: string,
  countryCode?: string | null,
): StaticSupportContentCard[] => {
  if (countryCode === "US") {
    return getUnitedStatesStaticSupportCategoryCards(slug, countryCode);
  }

  if (countryCode === "NZ") {
    return getNewZealandStaticSupportCategoryCards(slug, countryCode);
  }

  if (countryCode === "AU") {
    const definitions = AUSTRALIA_SUPPORT_CARD_DEFINITIONS[slug];
    return definitions ? buildCategoryCards(slug, definitions) : [];
  }

  return [];
};
